/* global require */
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar";
import { PlaySoundM } from "@common/components/playSound/PlaySoundM";
import { useLiveKitEventHandler } from "@common/hooks/livekit/useLiveKitEventHandler";
import { pauseActivity } from "@data/redux/actions/activity";
import { apiRequest } from "@data/redux/actions/api";
import {
    changeUnreadMessageInLiveCount,
    newMessage,
} from "@data/redux/actions/broadcast";
import {
    setActivityFinalizedCount,
    setCompletedConversation,
    setIsTyping,
    setRecentFinalizedActivity,
    setAutoModeStartedActivity,
    setTypingUser,
    clearCheckpointConversation,

} from "@data/redux/actions/liveMode";
import { authService } from "@data/services/authService";
import { liveServiceV2 } from "@data/services/liveServiceV2";
import { schoolAssessmentsService } from "@data/services/schoolAssessmentsService";
import { studentsService } from "@data/services/studentsService";
import { hasRole } from "@data/utility/ability";
import { hasFeature } from "@data/utility/Utils";
import { useNavigation } from "@react-navigation/native";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { markActivitiesAsDeleted } from "../../../data/redux/actions/activity";
import {
    setNewTodoActivity,
    setOngoingCheckpointIds,
} from "../../../data/redux/actions/liveMode";
import { liveService } from "../../../data/services/liveService";
import { hasSummaryToShow } from "@data/redux/reducers/liveMode";
import { useCurrentActivity } from "../useCurrentActivity";
import { fromLiveTodoPayload } from "../../../engine/live/room/liveTodoPayload";
import { isStudentLiveScreenMounted } from "../../../engine/live/student/liveScreenPresence";
import { isTutorLiveScreenMounted } from "../../../engine/live/tutor/tutorLiveScreenPresence";

export const uint8ArrayToString = (uint8Array) => {
    try {
        const decoder = new TextDecoder("utf-8", {
            fatal: false,
            ignoreBOM: true,
        })
        const encodedString = decoder.decode(uint8Array)
        return decodeURIComponent(encodedString)
    } catch (error) {
        // Simple fallback that preserves basic ASCII characters
        return Array.from(uint8Array)
            .filter(
                (byte) => byte >= 32 || byte === 9 || byte === 10 || byte === 13
            )
            .map((byte) => String.fromCharCode(byte))
            .join("")
    }
}

export const stringToUint8Array = (str) => {
    const arr = new Uint8Array(str.length)
    for (let i = 0; i < str.length; i++) {
        arr[i] = str.charCodeAt(i)
    }
    return arr
}

const useLiveKitEventListener = () => {
    const dispatch = useDispatch()
    const navigation = useNavigation()
    const clearTimeoutRef = useRef()
    const user = useSelector((state) => state.data.currentUser?.object)
    const liveSessionId = useSelector((state) => state.liveMode?.liveSessionId)
    const checkpointConversation = useSelector((state) => state.liveMode?.checkpointConversation)
    // Pendant une présentation (le partage du focus), cette snackbar nommerait un élève dans ce qui part
    // chez les élèves : on la tait. Lue par une ref, comme `user`, pour garder les gestionnaires stables.
    const isFocusScreenShare = useSelector((state) => state.liveMode?.isFocusScreenShare)
    const isFocusScreenShareRef = useRef(isFocusScreenShare)
    useEffect(() => {
        isFocusScreenShareRef.current = isFocusScreenShare
    }, [isFocusScreenShare])
    const { setCurrentActivity, currentActivity } = useCurrentActivity()

    // Stabilize `user` via a ref so the memoized handlers object does not
    // change identity every time the Redux backend poll (every 10-30s) returns
    // a new `currentUser` reference. Without this, the events object would be
    // invalidated and `useLiveKitEventHandler` would detach + re-attach all
    // RoomEvent listeners for the entire live session.
    const userRef = useRef(user)
    useEffect(() => {
        userRef.current = user
    }, [user])

    // Same reasoning for `currentActivity` and `setCurrentActivity`: they come
    // from `useCurrentActivity` and change identity when the student switches
    // activity. Reading them via refs keeps `tutorFinalizedActivity`,
    // `deleteTodo` and `checkpointTriggerResponse` observing the latest
    // activity without invalidating the events object on every switch.
    const currentActivityRef = useRef(currentActivity)
    useEffect(() => {
        currentActivityRef.current = currentActivity
    }, [currentActivity])
    const setCurrentActivityRef = useRef(setCurrentActivity)
    useEffect(() => {
        setCurrentActivityRef.current = setCurrentActivity
    }, [setCurrentActivity])

    const isRecipient = (studentId, senderId) => {
        const currentUser = userRef.current
        return (
            hasRole(currentUser, "student") &&
            studentId?.toString() === currentUser?.id?.toString() &&
            senderId !== currentUser?.id?.toString()
        )
    }

    const eventHandlers = useMemo(
        () => ({
            newMessage: (event, participant) => {
                const currentUser = userRef.current
                const senderId = participant.identity?.split(".")[0]?.toString()
                if (senderId !== currentUser?.id?.toString()) {
                    dispatch(newMessage(event))
                    // Un texte porte `threadId` ; une pièce jointe, la ressource du
                    // serveur (`thread_id`) : elle aussi allume la pastille.
                    dispatch(
                        changeUnreadMessageInLiveCount(
                            event.threadId ?? event.thread_id,
                            null
                        )
                    )
                    // L'écran du live de l'élève (design system) joue lui-même ce son.
                    if (!isStudentLiveScreenMounted()) {
                        PlaySoundM.play(
                            require("../../../../assets/sounds/toggle_001.mp3")
                        )
                    }
                }
            },
            finalizeLive: () => {
                const currentUser = userRef.current
                if (hasRole(currentUser, "student")) {
                    dispatch(pauseActivity(true))
                    // L'écran du live de l'élève (design system) mène lui-même à la fin.
                    if (!isStudentLiveScreenMounted()) {
                        setTimeout(() => {
                            navigation.navigate("end-session-student")
                        }, 2000)
                    }
                }
            },
            newTodo: (e, p) => {
                const currentUser = userRef.current
                const eventData = e.data || e
                const studentId = eventData?.id ?? null
                const activity = fromLiveTodoPayload(eventData)
                const forceFocus = eventData?.forceFocus ?? false
                const senderId = p?.identity?.split(".")[0]?.toString()

                // L'élève de l'événement : un envoi du serveur (l'activité que prépare le champion) n'a pas
                // d'expéditeur, et un autre masteur n'est pas l'élève.
                const listOwnerId = studentId ?? senderId
                if (
                    hasRole(currentUser, "tutor") &&
                    senderId !== currentUser?.id?.toString() &&
                    listOwnerId != null
                ) {
                    dispatch(
                        apiRequest(studentsService.getStudent, {
                            id: listOwnerId,
                        })
                    )
                }

                const recipientCheck = isRecipient(studentId, senderId)

                if (recipientCheck) {
                    // L'écran du live de l'élève (design system) relit lui-même le compte.
                    if (!isStudentLiveScreenMounted()) dispatch(apiRequest(authService.getUser))
                    if (activity) {
                        dispatch(setNewTodoActivity(activity, forceFocus))
                    } else {
                        console.warn("[newTodo] ⚠️ No activity object in event")
                    }
                }
            },
            // Unified delete handler - handles single, multiple, and all deletes
            deleteTodo: (e, p) => {
                const studentId = e?.id ?? null
                const senderId = p?.identity?.split(".")[0]?.toString()
                const currentActivity = currentActivityRef.current

                if (isRecipient(studentId, senderId)) {
                    apiRequest(authService.getUser)
                    const deletedIds = e?.activity_ids ?? []

                    if (deletedIds.length > 0) {
                        dispatch(markActivitiesAsDeleted(deletedIds))

                        if (
                            deletedIds.includes(
                                currentActivity?.activityData?.id
                            )
                        ) {
                            setCurrentActivityRef.current({
                                activityData: {
                                    ...currentActivity?.activityData,
                                    isDeleted: true,
                                },
                                progressStatus: currentActivity?.progressStatus,
                            })

                            if (checkpointConversation) {
                                const checkpointActivityId = checkpointConversation?.conversation?.activity_id
                                if (deletedIds.includes(checkpointActivityId)) {
                                    const conversationId = checkpointConversation?.conversation?.id
                                    const isOngoing = checkpointConversation?.conversation?.status === "ongoing"
                                    if (conversationId && isOngoing) {
                                        dispatch(apiRequest(
                                            liveService.finishConversation,
                                            { conversation_id: conversationId },
                                            { interruption_reason: "tutor_interruption", ended_at: new Date().toISOString() }
                                        ))
                                    }
                                    dispatch(clearCheckpointConversation())
                                }
                            }
                        }
                    }
                }
            },
            // Un ancien écran l'envoie encore, toujours après `newTodo`, qui relit déjà l'élève.
            refreshTodoListForTutor: () => {},
            activityFinalized: (e, p) => {
                const currentUser = userRef.current
                if (hasRole(currentUser, "tutor")) {
                    const userID = p?.identity?.split(".")[0]?.toString()
                    dispatch(setActivityFinalizedCount(liveSessionId, userID))
                    PlaySoundM.play(
                        require("../../../../assets/sounds/finished.mp3")
                    )
                    // Resolve liveRoom from the latest user snapshot + current
                    // liveSessionId. Computing this inside the handler keeps
                    // the events object stable across Redux currentUser polls.
                    const liveRoom = currentUser?.live_sessions?.find(
                        (s) => s?.id === liveSessionId,
                    )?.live_room
                    // Rooms on the events panel surface this as a card (and
                    // only those rooms read recentFinalizedActivities); for
                    // everyone else keep the legacy toast so the activity-
                    // finalized signal isn't silently lost.
                    if (hasFeature(liveRoom, "events-panel") || isTutorLiveScreenMounted()) {
                        dispatch(
                            setRecentFinalizedActivity({
                                studentId: parseInt(userID),
                                activityId: e?.activityId ?? null,
                                finishedAt: Date.now(),
                            }),
                        )
                    } else {
                        SnackBarM.show({
                            text1:
                                "🎯 " +
                                (e?.displayName ?? "L'élève") +
                                " a terminé une activité",
                            duration: 6000,
                        })
                    }
                    if (userID) {
                        dispatch(
                            apiRequest(studentsService.getStudent, {
                                id: userID,
                            })
                        )
                        // Refetch school assessments for this student
                        dispatch(
                            apiRequest(
                                schoolAssessmentsService.getSchoolAssessments,
                                {},
                                {},
                                { student_id: userID, upcoming: true }
                            )
                        )
                    }
                }
            },
            // Student in a group session self-evaluated and picked their next
            // activity on their own (auto-mode). Mirror activityFinalized: only
            // tutors on an events-panel room record the signal, which the panel
            // turns into an "a choisi sa prochaine activité" card with a
            // "Voir l'énoncé" CTA.
            autoModeActivityStarted: (e, p) => {
                const currentUser = userRef.current
                if (!hasRole(currentUser, "tutor")) return
                const liveRoom = currentUser?.live_sessions?.find(
                    (s) => s?.id === liveSessionId,
                )?.live_room
                // The design-system tutor room reads it too (its "auto_activity" card), whatever the room's labels.
                if (!hasFeature(liveRoom, "events-panel") && !isTutorLiveScreenMounted()) return
                const senderId = p?.identity?.split(".")[0]?.toString()
                if (!senderId) return
                dispatch(
                    setAutoModeStartedActivity({
                        studentId: parseInt(senderId),
                        evaluation: e?.evaluation ?? null,
                        activityId: e?.activityId ?? null,
                    })
                )
                // The student just created this activity on their own, so the
                // tutor's cached copy of the student doesn't have it yet. Refetch
                // (like activityFinalized does) so the new activity lands in the
                // student's todo_activities — otherwise the "Voir l'énoncé" CTA
                // resolves to nothing in TutorFocusLayoutM.
                dispatch(
                    apiRequest(studentsService.getStudent, {
                        id: senderId,
                    })
                )
            },
            tutorFinalizedActivity: (e, p) => {
                const studentId = e?.studentId
                const senderId = p?.identity?.split(".")[0]?.toString()
                const activityId = e?.activityId

                // Only handle if recipient is the student and sender is not the student
                if (isRecipient(studentId, senderId)) {
                    const xpGained = e?.xpGained ?? 0
                    const petals = e?.petals ?? 0
                    const currentActivity = currentActivityRef.current

                    // Activity data from tutor's finalization
                    const finishedAt = e?.finishedAt
                    const status = e?.status ?? "validated"
                    const duration = e?.duration
                    const xpGainedActivity = e?.xpGainedActivity

                    // Check if student is viewing this activity AND hasn't already submitted it
                    const isViewingThisActivity =
                        currentActivity?.activityData?.id === activityId
                    const alreadyFinished =
                        currentActivity?.activityData?.finished_at ||
                        currentActivity?.progressStatus === "finished"

                    // Only update activity status if student hasn't already submitted
                    if (isViewingThisActivity && !alreadyFinished) {
                        // L'écran du live de l'élève (design system) joue lui-même le son et le bandeau.
                        if (!isStudentLiveScreenMounted()) {
                            // Play success sound
                            PlaySoundM.play(
                                require("../../../../assets/sounds/finished.mp3")
                            )

                            // Show rewards snackbar to student
                            let message = `✅ Activité soumise ! ⚡ ${xpGained} XP`
                            if (petals > 0) {
                                message += `  🌸 ${petals} pétales`
                            }
                            SnackBarM.show({
                                text1: message,
                                duration: 6000,
                            })
                        }

                        // Update activity status to finished with data from backend
                        setCurrentActivityRef.current({
                            activityData: {
                                ...currentActivity?.activityData,
                                finished_at:
                                    finishedAt || new Date().toISOString(),
                                status: status,
                                duration:
                                    duration ??
                                    currentActivity?.activityData?.duration,
                                xp_gained: xpGainedActivity ?? xpGained,
                            },
                            progressStatus: "finished",
                        })
                    }

                    // L'écran du live de l'élève (design system) relit lui-même le compte.
                    if (!isStudentLiveScreenMounted()) dispatch(apiRequest(authService.getUser))
                }
            },
            typing: (e, p) => {
                const currentUser = userRef.current
                const id = currentUser?.id
                const senderId = p?.identity?.split(".")[0]?.toString()
                if (senderId?.toString() === id?.toString()) return

                if (clearTimeoutRef.current) {
                    clearTimeout(clearTimeoutRef.current)
                }
                dispatch(setTypingUser(parseInt(senderId)))
                dispatch(setIsTyping(true))
                clearTimeoutRef.current = setTimeout(() => {
                    dispatch(setIsTyping(false))
                }, 1500)
            },
            agentConversationCompleted: (e, p) => {
                const currentUser = userRef.current
                const senderId = p?.identity?.split(".")[0]?.toString()
                const liveSessionId = e?.liveSessionId
                const userDisplayName = e?.displayName
                const status = e?.status
                const conversation = e?.conversation

                if (hasRole(currentUser, "tutor")) {
                    // No summary, no popup: nothing to announce either.
                    const hasSummary = hasSummaryToShow(conversation?.conversation)
                    if (hasSummary)
                        PlaySoundM.play(
                            require("../../../../assets/sounds/jingles_STEEL16.mp3")
                        )
                    dispatch(setOngoingCheckpointIds([senderId], "remove"))
                    dispatch(
                        apiRequest(
                            liveServiceV2.getLiveSessionV2,
                            { id: liveSessionId },
                            {}
                        )
                    )

                    // La salle du tuteur (design system) le dit dans sa colonne ; un quiz n'y passe jamais pour le champion.
                    if (hasSummary && status === "ended" && !isFocusScreenShareRef.current && !isTutorLiveScreenMounted())
                        SnackBarM.show({
                            text1: `🤖Nouveau résumé de point d'avancement disponible pour ${userDisplayName}`,
                            duration: 6000,
                        })

                    dispatch(
                        setCompletedConversation(
                            liveSessionId,
                            parseInt(senderId),
                            conversation
                        )
                    )
                }
            },
            studentWorkUpdated: (event, participant) => {
                const currentUser = userRef.current
                const senderId = participant.identity?.split(".")[0]?.toString()
                if (
                    hasRole(currentUser, "tutor") &&
                    senderId !== currentUser?.id?.toString()
                ) {
                    const studentId = event?.studentId
                    if (studentId) {
                        dispatch(
                            apiRequest(studentsService.getStudent, {
                                id: studentId,
                            })
                        )
                    }
                }
            },
            checkpointTriggerResponse: (event) => {
                // L'écran du live de l'élève (design system) ouvre lui-même le champion que le tuteur envoie.
                if (isStudentLiveScreenMounted()) return
                const currentUser = userRef.current
                const {
                    studentId,
                    accepted,
                    trigger_context,
                    trigger_source,
                } = event

                if (studentId?.toString() !== currentUser?.id?.toString()) return

                if (accepted) {
                    const currentActivity = currentActivityRef.current

                    dispatch(
                        apiRequest(
                            liveService.createConversation,
                            {},
                            {
                                student_id: studentId,
                                live_session_id: liveSessionId,
                                activity: currentActivity?.activityData?.id,
                                trigger_context: trigger_context,
                                trigger_source: trigger_source,
                            }
                        )
                    )

                    dispatch(
                        apiRequest(
                            liveServiceV2.raiseHandV2,
                            { id: liveSessionId },
                            { raised: false }
                        )
                    )
                }
            },
        }),
        [dispatch, navigation, liveSessionId, checkpointConversation]
    )

    useLiveKitEventHandler(eventHandlers, uint8ArrayToString)
}

export default useLiveKitEventListener
